import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import { useLoaderData } from 'react-router';

import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { hasPendingInPageEnquiryAutoSubmit } from '~/modules/enquiry/util/pending-enquiry-auto-submit';
import { usePageCtas } from '~/modules/enquiry/component/PageCtasProvider';
import {
    useBookMeetingAvailability,
    type BookMeetingAvailability,
} from './use-book-meeting-availability';

/** Title of the (03) "Book a Meeting" accordion row. Shared so the summary CTA
 *  can open exactly this row in the controlled NumberedAccordion. */
export const BOOK_MEETING_ROW_TITLE = 'Like this Project? Book a Meeting';

/** DOM id on the "Book a Meeting" accordion row, used as the CTA scroll anchor. */
export const BOOK_MEETING_ROW_ID = 'book-a-meeting';

/** DOM id on the About the Project section (summary "Read more" anchor). */
export const ABOUT_PROJECT_ROW_ID = 'about-the-project';

interface BookMeetingAccordionValue {
    /** Controlled open-row titles for the project accordion (multiple mode). */
    openRows: string[];
    setOpenRows: (value: string[]) => void;
    /** Open the "Book a Meeting" row and smooth-scroll it into view. */
    focusBookMeeting: () => void;
    /** Collapse just the "Book a Meeting" row (leaves other rows open). */
    closeBookMeeting: () => void;
    /** Smooth-scroll About the Project into view (the summary's "Read more"
     *  target). A section of its own above the accordion, so nothing opens. */
    focusAboutProject: () => void;
    /**
     * Eager availability prefetch for the project's booking row. Owned here so it
     * runs once on page load (the provider is always mounted) — the calendar is
     * ready the moment the (03) row opens instead of fetching on mount. The
     * project accordion passes this into BookMeetingRowContent as controlled
     * state; the professional detail page has no provider and self-fetches.
     * (BC-4603)
     */
    meetingAvailability: BookMeetingAvailability;
}

const BookMeetingAccordionContext =
    createContext<BookMeetingAccordionValue | null>(null);

/**
 * Coordinates the project-detail "Book a Meeting" CTA (in the summary row) with
 * the (03) accordion row lower on the page: clicking the CTA opens that row and
 * scrolls to it, rather than opening a separate modal (BC-4603 inline booking).
 *
 * Mount inside the page's `PageCtasProvider`: the Book a meeting button it
 * resolves decides which sessions are prefetched.
 */
export const BookMeetingAccordionProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    // Every row starts collapsed (stakeholder call: the page reads as a clean
    // numbered index).
    const [openRows, setOpenRows] = useState<string[]>([]);

    // Single eager availability fetch for the whole page (see context docs):
    // the page's Book a meeting sessions, while that button opens the gate.
    const { book } = usePageCtas().ctas;
    const sessionKey =
        book.visible && book.viaGate ? book.sessionIds.join(',') : '';
    const sessions = useMemo(
        () =>
            sessionKey
                ? sessionKey.split(',').map((id) => ({ ID: Number(id) }))
                : [],
        [sessionKey],
    );
    const meetingAvailability = useBookMeetingAvailability(sessions);
    const { project } = useLoaderData<typeof loader>();

    const focusBookMeeting = useCallback(() => {
        setOpenRows((prev) =>
            prev.includes(BOOK_MEETING_ROW_TITLE)
                ? prev
                : [...prev, BOOK_MEETING_ROW_TITLE],
        );
        // Defer a frame so the row is open (and laid out) before we scroll to it.
        requestAnimationFrame(() => {
            document
                .getElementById(BOOK_MEETING_ROW_ID)
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }, []);

    const closeBookMeeting = useCallback(() => {
        setOpenRows((prev) =>
            prev.filter((title) => title !== BOOK_MEETING_ROW_TITLE),
        );
    }, []);

    const focusAboutProject = useCallback(() => {
        document
            .getElementById(ABOUT_PROJECT_ROW_ID)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    // A guest's enquiry from the (03) row hits the auth wall, and completing
    // auth reloads the document — resetting this in-memory open-row state and
    // closing the row mid-flow. When that pending enquiry belongs to this
    // project, reopen the row and scroll it back into view so the
    // auto-submitted enquiry's confirmation is visible. Runs before the
    // root-level auto-submit hook clears the pending flag (child effects fire
    // before ancestors'), and re-adding an already-open row is a no-op.
    useEffect(() => {
        if (!hasPendingInPageEnquiryAutoSubmit(project.ID)) return;
        focusBookMeeting();
    }, [project.ID, focusBookMeeting]);

    const value = useMemo(
        () => ({
            openRows,
            setOpenRows,
            focusBookMeeting,
            closeBookMeeting,
            focusAboutProject,
            meetingAvailability,
        }),
        [
            openRows,
            focusBookMeeting,
            closeBookMeeting,
            focusAboutProject,
            meetingAvailability,
        ],
    );

    return (
        <BookMeetingAccordionContext.Provider value={value}>
            {children}
        </BookMeetingAccordionContext.Provider>
    );
};

export const useBookMeetingAccordion = () => {
    const context = useContext(BookMeetingAccordionContext);
    if (!context) {
        throw new Error(
            'useBookMeetingAccordion must be used within a BookMeetingAccordionProvider',
        );
    }
    return context;
};

/**
 * Provider-tolerant variant: returns null instead of throwing when there's no
 * {@link BookMeetingAccordionProvider} above. Lets the shared booking row render
 * on the professional detail page (which has no project accordion to coordinate)
 * while the project page still gets the row-collapse behaviour.
 */
export const useBookMeetingAccordionOptional = () =>
    useContext(BookMeetingAccordionContext);
